import type { CSSProperties, FC, ReactNode } from 'react'

import Image from 'next/image'
import { cn } from '@/lib/utils'

/*
 * The site's portraits: six photos of designers, shipped with the site in public/images/designers
 * so they never depend on the site's web address. Every person on the site is one of these
 * photos, never a drawing. `face` is where the face sits in the square photo, so tight crops
 * (avatars) can zoom in on it.
 */

export type PhotoId = 'luca' | 'elena' | 'sofia' | 'mateo' | 'adrian' | 'nora'

interface PhotoSource {
    src: string
    face: string
}

const FOLDER = '/images/designers'

export const PHOTOS: Record<PhotoId, PhotoSource> = {
    luca: { src: `${FOLDER}/luca-moretti.png`, face: '57% 34%' },
    elena: { src: `${FOLDER}/elena-varga.png`, face: '60% 33%' },
    sofia: { src: `${FOLDER}/sofia-marin.png`, face: '64% 35%' },
    mateo: { src: `${FOLDER}/mateo-alvarez.png`, face: '62% 33%' },
    adrian: { src: `${FOLDER}/adrian-keller.png`, face: '55% 36%' },
    nora: { src: `${FOLDER}/nora-hale.png`, face: '63% 35%' }
}

export const PHOTO_IDS: PhotoId[] = ['luca', 'elena', 'sofia', 'mateo', 'adrian', 'nora']

/* The people named across the site, so the same name always shows the same face */
const KNOWN: Record<string, PhotoId> = {
    'Maren Holt': 'elena',
    'Teodor Valk': 'luca',
    'Priya Nandakumar': 'sofia',
    'Ines Calloway': 'nora',
    'Tomás Vidal': 'mateo',
    'Daniel Osei': 'adrian',
    'Mira Solberg': 'nora',
    'Ana Ribeiro': 'sofia',
    'June Park': 'elena',
    'Hanna Ek': 'nora'
}

/** Picks a photo for a name, the same one every time. */
export const photoForName = (name: string): PhotoId => {
    if (KNOWN[name]) return KNOWN[name]

    const hash = name.split('').reduce((total, character) => (total * 31 + character.charCodeAt(0)) >>> 0, 7)

    return PHOTO_IDS[hash % PHOTO_IDS.length]
}

interface PhotoProps {
    id: PhotoId
    /** Describe the person when no name is shown next to the photo; leave empty when it is. */
    alt?: string
    /** How wide the photo renders, so the right size is downloaded, for example '(min-width: 768px) 33vw, 100vw'. */
    sizes: string
    /** Zoom in on the face, for avatars and small crops. */
    portrait?: boolean
    priority?: boolean
    className?: string
    style?: CSSProperties
}

/** A photo filling its box. Give the box a size (or an aspect ratio) through `className`. */
export const Photo: FC<PhotoProps> = ({ id, alt = '', sizes, portrait = false, priority = false, className, style }): ReactNode => {
    const photo = PHOTOS[id]

    return (
        <span className={cn('relative block overflow-hidden bg-pane', className)} style={style}>
            <Image
                src={photo.src}
                alt={alt}
                fill
                sizes={sizes}
                priority={priority}
                className='object-cover'
                style={portrait ? { objectPosition: photo.face, scale: '2.1', transformOrigin: photo.face } : { objectPosition: photo.face }}
            />
        </span>
    )
}
